/**
 * Parts Master filter row.
 *
 * Extracted from `PartsMaster.tsx` so the page component stays under the
 * 500-line cap. Pure presentation: search, status, commodity type, and
 * (PNA-only) default warehouse.
 */
import { Search, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { FilterSection, FilterGrid, FilterField } from '../filters/FilterSection';
import { Input } from '../ui/input';
import SearchableSelect from '../ui/SearchableSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface CommodityType {
  id: number;
  name: string;
}

interface PartsMasterFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  commodityGroupFilter: string;
  onCommodityChange: (value: string) => void;
  commodityOptions: CommodityType[];
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  /** Show the "Default Warehouse" Select. PNA-only, also requires data. */
  showWarehouseFilter: boolean;
  warehouseFilter: string;
  onWarehouseChange: (value: string) => void;
  warehouseOptions: string[];
  /** Right-side toolbar slot — renders the Columns + Export buttons. */
  toolbarSlot?: React.ReactNode;
}

const ALL_VALUE = 'all';

/**
 * Render the Parts Master filter / search / pagination-size row.
 *
 * @param toolbarSlot - Right-aligned controls (Columns toggle, Export button).
 */
export default function PartsMasterFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  commodityGroupFilter,
  onCommodityChange,
  commodityOptions,
  pageSize,
  onPageSizeChange,
  showWarehouseFilter,
  warehouseFilter,
  onWarehouseChange,
  warehouseOptions,
  toolbarSlot,
}: PartsMasterFiltersProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <FilterSection title="Search & Filters" icon={SlidersHorizontal} bare>
          <FilterGrid>
            <FilterField label="Search">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Part number, description..."
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </FilterField>

            <FilterField label="Status">
              <Select value={statusFilter} onValueChange={onStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Commodity Type">
              {/* Typeable: Barratt has ~150 commodity codes, so the plain
                  dropdown meant scrolling. Empty value = every type. */}
              <div data-testid="commodity-type-filter">
                <SearchableSelect
                  options={commodityOptions.map((cg) => ({ value: String(cg.id), label: cg.name }))}
                  value={commodityGroupFilter === ALL_VALUE ? '' : commodityGroupFilter}
                  onChange={(v) => onCommodityChange(v || ALL_VALUE)}
                  placeholder="All Types"
                />
              </div>
            </FilterField>

            {showWarehouseFilter && (
              <FilterField label="Default Warehouse">
                <Select value={warehouseFilter} onValueChange={onWarehouseChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Default Warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_VALUE}>All Warehouses</SelectItem>
                    {warehouseOptions.map((wh) => (
                      <SelectItem key={wh} value={wh}>
                        {wh}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            )}
          </FilterGrid>

          {/* Row density + right-aligned toolbar (Columns, Export). */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => onPageSizeChange(Number(v))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {toolbarSlot && (
              <div className="flex items-center gap-2 ml-auto">
                {toolbarSlot}
              </div>
            )}
          </div>
        </FilterSection>
      </CardContent>
    </Card>
  );
}
